import { t } from "@lingui/macro";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { ScrollView } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { SubtleInput } from "@whoosh/design-system/src/components/controls/SubtleInput";
import { Loader } from "@whoosh/design-system/src/components/display";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { ProceedToRequestReceivedStepAtom } from "../../../../atoms/actions";
import { VerificationModalStepStateAtom } from "../../../../atoms/stores";
import { useActivateAccount } from "./hooks/useActivateAccount";

const verificationStepDataAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  if (state.step === "ActivateAccount") {
    return state;
  }
  return null;
});

export const ActivateYourAccountStep = () => {
  const verificationStepData = useAtomValue(verificationStepDataAtom);
  const [email, setEmail] = useState(verificationStepData?.email ?? "");

  const { showToast } = useToast();

  const goToRequestReceived = useSetAtom(ProceedToRequestReceivedStepAtom);

  const onActivateAccountComplete = () => {
    goToRequestReceived({
      email: email,
    });
  };

  const onActivateAccountError = () => {
    showToast({
      description: t`Something went wrong. Please try again.`,
      semanticStyle: "error",
      toastId: "activate-account-error",
    });
  };

  const [activateAccount, isActiviating] = useActivateAccount({
    onCompleted: onActivateAccountComplete,
    onError: onActivateAccountError,
  });

  const handleActivateAccount = () => {
    activateAccount({ email });
  };

  return (
    <Box flex={1} paddingHorizontal={6}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack paddingBottom={6} flexGrow={1} position="relative">
          {isActiviating ? <Loader /> : null}
          <VStack flexGrow={1}>
            <VStack mb={8} gap={4}>
              <Text variant={"mainTitle2"}>{t`Activate your account`}</Text>
              <Text
                variant="mainBaseMedium"
                color="text-secondary"
              >{t`Please enter the email address that your club has on file for you.`}</Text>
            </VStack>
            <SubtleInput
              placeholder={t`Email`}
              name={"email"}
              value={email}
              onChange={setEmail}
              keyboardType="email-address"
            />
          </VStack>
        </VStack>
      </ScrollView>
      <Button
        isDisabled={!email}
        onPress={handleActivateAccount}
        accessibilityLabel={t`Send Verification Email button`}
      >{t`Send Verification Email`}</Button>
    </Box>
  );
};
